import React, { useState, useEffect, useRef, lazy, Suspense } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import Select from "react-select";
import CreatableSelect from "react-select/creatable";
import "./TaskForm.css";
import { api } from "../api/client";
import {
  createTeamsMeeting
} from "../utils/teamsUtils";
import { useWorkspaceRoster, rosterMentionSuggestions, rosterParseMentions } from "../hooks/useWorkspaceRoster";
import { useCurrentUser } from "../contexts/CurrentUserContext";
import LocationInput from "./LocationInput";
import { getTravelInfo, MODE_EMOJI } from "../services/travelTimeService";

const LogTimeModal = lazy(() => import('./TimeTracking/LogTimeModal'));


// Types that auto-set the task as private (hidden from team members)
const PERSONAL_TYPES = new Set(["Health", "Family", "Vacation", "Sick", "Personal"]);

// Types that switch the form into Leave Request mode
const LEAVE_REQUEST_TYPES = new Set(["Vacation", "Sick", "Personal"]);

// Maps task type value → backend leave type
const LEAVE_TYPE_MAP = { Vacation: 'vacation', Sick: 'sick', Personal: 'personal' };

// Count Mon–Fri days between two dates (inclusive)
function calcWorkingDays(start, end) {
  if (!start || !end) return 0;
  let count = 0;
  const curr = new Date(start);
  const last = new Date(end);
  curr.setHours(0, 0, 0, 0);
  last.setHours(0, 0, 0, 0);
  while (curr <= last) {
    const d = curr.getDay();
    if (d !== 0 && d !== 6) count++;
    curr.setDate(curr.getDate() + 1);
  }
  return count;
}

function TaskForm({
  title,
  setTitle,
  details,
  setDetails,
  priority,
  setPriority,
  startDate,
  setStartDate,
  startTime,
  setStartTime,
  deadlineDate,
  setDeadlineDate,
  deadlineTime,
  setDeadlineTime,
  taskType,
  setTaskType,
  assignedTeam,
  setAssignedTeam,
  householdMemberId,
  setHouseholdMemberId,
  reminderOffset,
  setReminderOffset,
  busy,
  setBusy,
  isPersonal,
  setIsPersonal,
  meetingType,
  setMeetingType,
  location,
  setLocation,
  recurrence,
  setRecurrence,
  recurEditScope,
  setRecurEditScope,
  project,
  setProject,
  editingId,
  onSubmit,
  onCancel,
  taskTypes,
  clashWarning,
  onSaveAnyway,
  onChangeTime,
}) {
  const { currentUser, workspaces, activeWorkspaceId } = useCurrentUser();
  const isPro = ["Pro", "Team", "Enterprise"].includes(currentUser?.plan);
  const [showLogTime, setShowLogTime] = useState(false);
  const [dateRangeError, setDateRangeError] = useState(null);

  // Same derivation as App.js:311 — no new prop threaded, workspaces/activeWorkspaceId
  // are already available via the shared CurrentUserContext.
  const activeWorkspace = (workspaces || []).find(w => w.id === activeWorkspaceId) || null;
  const isPersonalWorkspace = activeWorkspace?.type === 'personal';

  const [householdMembers, setHouseholdMembers] = useState([]);

  // Load household members for the Personal/Family "For" picker — mirrors the
  // "Load available users" effect below, just gated to personal workspaces.
  useEffect(() => {
    if (!isPersonalWorkspace) return;
    api('/api/household')
      .then(async res => {
        const d = res.ok ? await res.json() : null;
        setHouseholdMembers(d?.members || []);
      })
      .catch(() => {});
  }, [isPersonalWorkspace]);

  // Validate on every start/end prop change, including when edit form first opens
  // with a pre-inverted task (dateRangeError would otherwise stay null on mount).
  useEffect(() => {
    const s = combineDT(startDate, startTime);
    const e = combineDT(deadlineDate, deadlineTime);
    setDateRangeError(s && e && e < s ? 'End date is before the start date' : null);
  }, [startDate, startTime, deadlineDate, deadlineTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Combines a Date (date part) and a Date (time part) into one comparable Date.
  function combineDT(date, time) {
    if (!date) return null;
    const d = new Date(date);
    if (time) {
      d.setHours(time.getHours(), time.getMinutes(), 0, 0);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    return d;
  }

  // Called when the START date picker changes.
  // If the new start would land after the existing end, auto-shift the end by
  // preserving the previous duration (calendar-app convention).
  function handleStartDateChange(newDate) {
    const oldStart = combineDT(startDate, startTime);
    const end      = combineDT(deadlineDate, deadlineTime);
    const newStart = combineDT(newDate, startTime);

    setStartDate(newDate);

    if (newStart && end && newStart > end) {
      const duration = (oldStart && end > oldStart) ? (end - oldStart) : 0;
      const newEnd   = new Date(newStart.getTime() + (duration > 0 ? duration : 0));
      setDeadlineDate(new Date(newEnd));
      if (deadlineTime) setDeadlineTime(new Date(newEnd));
      setDateRangeError(null);
    } else {
      setDateRangeError(null);
    }
  }

  // Called when the START time picker changes — same auto-shift logic.
  function handleStartTimeChange(newTime) {
    const oldStart = combineDT(startDate, startTime);
    const end      = combineDT(deadlineDate, deadlineTime);
    const newStart = combineDT(startDate, newTime);

    setStartTime(newTime);

    if (newStart && end && newStart > end) {
      const duration = (oldStart && end > oldStart) ? (end - oldStart) : 0;
      const newEnd   = new Date(newStart.getTime() + (duration > 0 ? duration : 0));
      setDeadlineDate(new Date(newEnd));
      if (deadlineTime) setDeadlineTime(new Date(newEnd));
      setDateRangeError(null);
    } else {
      setDateRangeError(null);
    }
  }

  // Called when the END date picker changes — show error, never auto-fix.
  function handleDeadlineDateChange(newDate) {
    setDeadlineDate(newDate);
    if (!newDate) { setDateRangeError(null); return; }
    const start  = combineDT(startDate, startTime);
    const newEnd = combineDT(newDate, deadlineTime);
    setDateRangeError(start && newEnd && newEnd < start
      ? 'End date is before the start date'
      : null);
  }

  // Called when the END time picker changes — show error, never auto-fix.
  function handleDeadlineTimeChange(newTime) {
    setDeadlineTime(newTime);
    if (!newTime) return;
    const start  = combineDT(startDate, startTime);
    const newEnd = combineDT(deadlineDate, newTime);
    setDateRangeError(start && newEnd && newEnd < start
      ? 'End date is before the start date'
      : null);
  }

  // Get user's default meeting type preference
  const getUserDefaultMeetingType = () => {
    try {
      const userProfile = JSON.parse(localStorage.getItem("userProfile"));
      return userProfile?.preferences?.defaultMeetingType;
    } catch (error) {
      console.warn("Error parsing user profile:", error);
      return null;
    }
  };

  const defaultMeetingType = getUserDefaultMeetingType();

  // Zoom integration state
  const [isZoomGenerating, setIsZoomGenerating] = useState(false);
  const [zoomConnected, setZoomConnected] = useState(false);

  // Google Meet integration state
  const [isGoogleMeetGenerating, setIsGoogleMeetGenerating] = useState(false);
  const [googleMeetConnected, setGoogleMeetConnected] = useState(false);


  // Teams integration state
  const [isTeamsGenerating, setIsTeamsGenerating] = useState(false);
  const [teamsConnected, setTeamsConnected] = useState(false);

  // Recurrence local state
  const [recurEnabled, setRecurEnabled] = useState(false);
  const [recurFrequency, setRecurFrequency] = useState('daily');
  const [recurInterval, setRecurInterval] = useState(1);
  const [recurUnit, setRecurUnit] = useState('days');
  const [recurDay, setRecurDay] = useState('monday');
  const [recurDate, setRecurDate] = useState(1);
  const [recurEndType, setRecurEndType] = useState('never');
  const [recurCount, setRecurCount] = useState(1);
  const [recurUntil, setRecurUntil] = useState(null);

  // Location autocomplete ref + travel time state
  const [showTravelTime, setShowTravelTime] = useState(false);
  const [travelFrom, setTravelFrom] = useState('');
  const [customTravelAddress, setCustomTravelAddress] = useState('');
  const [travelInfo, setTravelInfo] = useState(null);
  const [travelInfoLoading, setTravelInfoLoading] = useState(false);

  // Initialize recurrence local state from prop (for edit mode)
  useEffect(() => {
    if (recurrence) {
      setRecurEnabled(true);
      const type = (recurrence.type || recurrence.frequency || 'daily').toLowerCase();
      setRecurFrequency(type);
      setRecurInterval(recurrence.interval || 1);
      if (recurrence.day) setRecurDay(recurrence.day.toLowerCase());
      if (recurrence.date) setRecurDate(recurrence.date);
      if (recurrence.unit) setRecurUnit(recurrence.unit);
      if (recurrence.count) { setRecurEndType('count'); setRecurCount(recurrence.count); }
      else if (recurrence.until) { setRecurEndType('until'); setRecurUntil(new Date(recurrence.until)); }
      else setRecurEndType('never');
    } else {
      setRecurEnabled(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync local recurrence state -> parent recurrence prop
  useEffect(() => {
    if (!setRecurrence) return;
    if (!recurEnabled) {
      setRecurrence(null);
      return;
    }
    const obj = { type: recurFrequency, interval: recurFrequency === 'custom' ? recurInterval : 1 };
    if (recurFrequency === 'weekly') obj.day = recurDay;
    if (recurFrequency === 'monthly') obj.date = recurDate;
    if (recurFrequency === 'custom') obj.unit = recurUnit;
    if (recurEndType === 'count') obj.count = recurCount;
    if (recurEndType === 'until' && recurUntil) obj.until = recurUntil.toISOString();
    setRecurrence(obj);
  }, [recurEnabled, recurFrequency, recurInterval, recurUnit, recurDay, recurDate, recurEndType, recurCount, recurUntil, setRecurrence]);

  // Auto-set isPersonal when a personal task type is selected
  useEffect(() => {
    if (setIsPersonal) setIsPersonal(PERSONAL_TYPES.has(taskType));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [taskType]);

  // Load projects for the project dropdown
  const [projects, setProjects] = useState([]);
  useEffect(() => {
    try {
      const { default: projectManager } = require("../models/projects");
      setProjects(projectManager.getAllProjects());
    } catch { setProjects([]); }
  }, []);


  // Team assignment roster — active workspace's people (business → team members).
  const { members: availableUsers } = useWorkspaceRoster();
  const [selectedUsers, setSelectedUsers] = useState([]);

  // ✅ @MENTION FUNCTIONALITY STATE
  const [mentionSuggestions, setMentionSuggestions] = useState([]);
  const [showMentionDropdown, setShowMentionDropdown] = useState(false);
  const [mentionQuery, setMentionQuery] = useState('');
  const [mentionStartIndex, setMentionStartIndex] = useState(-1);
  const [detailsWithMentions, setDetailsWithMentions] = useState(details || '');

  // Mention resolution now runs against the active-workspace roster (availableUsers);
  // notifyUserMentioned stays on the context (unchanged contract).
  const { notifyUserMentioned } = useCurrentUser();

  // React-Select options for team assignment
  const teamMemberOptions = availableUsers.map(user => ({
    value: user.id,
    label: user.email ? `${user.name} (${user.email})` : user.name,
    user: user
  }));

  const selectedTeamMembers = teamMemberOptions.filter(option =>
    selectedUsers.includes(option.value)
  );

  // Sync selectedUsers from assignedTeam. Ids are real user UUIDs (strings) —
  // carry them as-is; the old parseInt coercion (mock numeric ids) would drop
  // every real assignee on edit.
  useEffect(() => {
    if (assignedTeam && Array.isArray(assignedTeam)) {
      setSelectedUsers(assignedTeam.map(String));
    } else {
      setSelectedUsers([]);
    }
  }, [assignedTeam]);

  // React-Select team assignment handler
  const handleTeamAssignmentChange = (selectedOptions) => {
    const userIds = selectedOptions ? selectedOptions.map(option => option.value) : [];
    setSelectedUsers(userIds);
    setAssignedTeam(userIds);
  };

  // ✅ @MENTION HANDLING METHODS
  const handleDescriptionChange = (e) => {
    const newText = e.target.value;
    const cursorPosition = e.target.selectionStart;

    setDetailsWithMentions(newText);
    setDetails(newText);

    // Check for @mention trigger
    const textBeforeCursor = newText.substring(0, cursorPosition);
    const mentionMatch = textBeforeCursor.match(/@([a-zA-Z0-9\s]*)$/);

    if (mentionMatch) {
      const query = mentionMatch[1];
      setMentionQuery(query);
      setMentionStartIndex(cursorPosition - mentionMatch[0].length);

      // Get mention suggestions
      const suggestions = rosterMentionSuggestions(availableUsers, query);
      setMentionSuggestions(suggestions);
      setShowMentionDropdown(suggestions.length > 0);
    } else {
      setShowMentionDropdown(false);
      setMentionSuggestions([]);
      setMentionQuery('');
      setMentionStartIndex(-1);
    }
  };

  const handleMentionSelect = (selectedUser) => {
    if (mentionStartIndex === -1) return;

    const beforeMention = detailsWithMentions.substring(0, mentionStartIndex);
    const afterCursor = detailsWithMentions.substring(mentionStartIndex + mentionQuery.length + 1);
    const mentionText = `@${selectedUser.name}`;

    const newText = beforeMention + mentionText + afterCursor;
    setDetailsWithMentions(newText);
    setDetails(newText);

    // Also add to assigned team if not already assigned
    if (!selectedUsers.includes(selectedUser.id)) {
      setSelectedUsers(prev => [...prev, selectedUser.id]);
    }

    // Hide dropdown
    setShowMentionDropdown(false);
    setMentionSuggestions([]);
    setMentionQuery('');
    setMentionStartIndex(-1);
  };

  const handleMentionKeyDown = (e) => {
    if (!showMentionDropdown || mentionSuggestions.length === 0) return;

    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
    } else if (e.key === 'Enter' && mentionSuggestions.length > 0) {
      e.preventDefault();
      handleMentionSelect(mentionSuggestions[0]);
    } else if (e.key === 'Escape') {
      setShowMentionDropdown(false);
      setMentionSuggestions([]);
    }
  };

  // Process mentions on form submission
  const processMentionsOnSubmit = () => {
    const mentions = rosterParseMentions(availableUsers, detailsWithMentions);

    // Notify mentioned users
    mentions.forEach(mention => {
      if (mention.userId !== currentUser?.id) {
        notifyUserMentioned(mention.userId, {
          type: 'task',
          title: title || 'New Task',
          description: detailsWithMentions,
          assignedBy: currentUser?.name
        });
      }
    });

    return mentions;
  };

  // Block D2 fix: connection status comes from the SERVER (VideoConnection) — the
  // same source the Settings > Video meetings page uses — not client localStorage.
  // The old isZoomConnected()/isGoogleMeetAvailable()/isTeamsConnected() checks read
  // localStorage, which the server-side OAuth flow never populates, so every provider
  // falsely showed "not connected". Fetched once on mount for all three.
  useEffect(() => {
    let cancelled = false;
    api('/api/connections/video')
      .then(r => r.json())
      .then(d => {
        if (cancelled) return;
        const list = Array.isArray(d.connections) ? d.connections : [];
        const have = (p) => list.some(c => c.provider === p);
        setZoomConnected(have('zoom'));
        setGoogleMeetConnected(have('meet'));
        setTeamsConnected(have('teams'));
      })
      .catch(() => {});
    return () => { cancelled = true; };
  }, []);

  // Handle Zoom meeting generation
  const handleGenerateZoomLink = async () => {
    if (!zoomConnected) {
      alert('Connect Zoom first in Settings → Video meetings.');
      return;
    }

    setIsZoomGenerating(true);
    try {
      // Server-side create via the stored VideoConnection (Block D2 fix), not the
      // deprecated client/localStorage path.
      const start = combineDT(startDate, startTime) || new Date();
      const pad = n => String(n).padStart(2, '0');
      const startISO = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}T${pad(start.getHours())}:${pad(start.getMinutes())}:00`;
      const res = await api('/api/connections/video/zoom/create-meeting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: title || 'Aidey Meeting', startTime: startISO, durationMinutes: 60 }),
      });
      const meeting = await res.json();
      if (!res.ok || !meeting.joinUrl) throw new Error(meeting.error || 'Zoom returned no link');
      setLocation(meeting.joinUrl);
      alert(`Zoom meeting created. Meeting ID: ${meeting.meetingId}`);
    } catch (error) {
      console.error('Error generating Zoom link:', error);
      alert(`Failed to create Zoom meeting: ${error.message}`);
    } finally {
      setIsZoomGenerating(false);
    }
  };

  // Handle Google Meet generation
  const handleGenerateGoogleMeetLink = async () => {
    if (!googleMeetConnected) {
      alert('Please connect your Google Calendar first to enable Google Meet generation.');
      return;
    }

    setIsGoogleMeetGenerating(true);
    try {
      // Server-side (Block D5): creates a real Google Calendar event with
      // conferenceData so Google mints a Meet link. Uses combineDT so the actual
      // picked date+time is sent (the old client path fed a Date into a string
      // template → "Invalid Date").
      const start = combineDT(startDate, startTime) || new Date();
      const pad = n => String(n).padStart(2, '0');
      const startISO = `${start.getFullYear()}-${pad(start.getMonth() + 1)}-${pad(start.getDate())}T${pad(start.getHours())}:${pad(start.getMinutes())}:00`;
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const res = await api('/api/connections/video/meet/create-meeting', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ topic: title || 'Aidey Meeting', startTime: startISO, durationMinutes: 60, timeZone }),
      });
      const meeting = await res.json();
      if (!res.ok || !meeting.meetLink) throw new Error(meeting.error || 'No Meet link returned');
      setLocation(meeting.meetLink);
      alert('Google Meet created and added to your Google Calendar.');
    } catch (error) {
      console.error('Error generating Google Meet link:', error);
      alert(`Failed to create Google Meet: ${error.message}`);
    } finally {
      setIsGoogleMeetGenerating(false);
    }
  };

  // Handle Teams meeting generation
  const handleGenerateTeamsLink = async () => {
    if (!teamsConnected) {
      alert('Please connect your Microsoft Outlook account first to enable Teams generation.');
      return;
    }

    setIsTeamsGenerating(true);
    try {
      const meetingDetails = {
        title: title || "Aidey Meeting",
        date: startDate?.toISOString().split('T')[0],
        time: startTime,
        duration: 60, // Default 1 hour
        details: details || `Microsoft Teams meeting for: ${title || "Aidey Meeting"}`
      };

      const meeting = await createTeamsMeeting(meetingDetails);
      if (meeting.success && meeting.meetingLink) {
        setLocation(meeting.meetingLink);
        alert(`Microsoft Teams meeting created successfully! Meeting added to your Outlook calendar.`);
      } else {
        throw new Error(meeting.error || 'Failed to create Teams meeting');
      }
    } catch (error) {
      console.error('Error generating Teams link:', error);
      alert(`Failed to create Teams meeting: ${error.message}`);
    } finally {
      setIsTeamsGenerating(false);
    }
  };

  // Validation function for location based on meeting type
  const validateLocation = () => {
    if (!meetingType || !location) return true; // No validation needed if no meeting type or location

    if (meetingType === "Address") {
      return location.trim().length > 0;
    }

    if (["Zoom", "Google Meet", "Teams", "Custom Link"].includes(meetingType)) {
      // Basic URL validation
      try {
        new URL(location);
        return true;
      } catch {
        return false;
      }
    }

    return true;
  };

  // Leave request mode state
  const isLeaveMode = LEAVE_REQUEST_TYPES.has(taskType);
  const [leaveAllowance, setLeaveAllowance] = useState(null);
  const [isLeaveLoadingAllowance, setIsLeaveLoadingAllowance] = useState(false);

  useEffect(() => {
    if (!isLeaveMode) { setLeaveAllowance(null); return; }
    setIsLeaveLoadingAllowance(true);
    api('/api/leave/allowance')
      .then(r => r.ok ? r.json() : null)
      .then(data => { if (data) setLeaveAllowance(data); })
      .catch(() => {})
      .finally(() => setIsLeaveLoadingAllowance(false));
  }, [isLeaveMode]);

  const workingDays = isLeaveMode ? calcWorkingDays(startDate, deadlineDate) : 0;

  // Travel time auto-lookup when location + travelFrom are set
  useEffect(() => {
    if (!showTravelTime || !location) { setTravelInfo(null); return; }
    const travelPrefs = currentUser?.preferences?.travel || {};
    let origin = '';
    if (travelFrom === 'home') origin = travelPrefs.homeAddress || '';
    else if (travelFrom === 'office') origin = travelPrefs.workplaceAddress || '';
    else if (travelFrom === 'custom') origin = customTravelAddress;
    if (!origin) { setTravelInfo(null); return; }
    let cancelled = false;
    setTravelInfoLoading(true);
    setTravelInfo(null);
    getTravelInfo(origin, location)
      .then(result => { if (!cancelled) { setTravelInfo(result); setTravelInfoLoading(false); } })
      .catch(() => { if (!cancelled) setTravelInfoLoading(false); });
    return () => { cancelled = true; };
  }, [location, travelFrom, customTravelAddress, showTravelTime]); // eslint-disable-line react-hooks/exhaustive-deps

  // Pending attachments (create mode — queued until task ID is known)
  const [pendingFiles, setPendingFiles] = useState([]);
  const [pendingLinks, setPendingLinks] = useState([]);
  const [linkInput, setLinkInput] = useState('');
  const pendingFileInputRef = useRef(null);

  const handleAddPendingLink = () => {
    const val = linkInput.trim();
    if (!val) return;
    setPendingLinks(prev => [...prev, val]);
    setLinkInput('');
  };

  // Attachments (edit mode only)
  const [taskAttachments, setTaskAttachments] = useState([]);
  const [attachmentUploading, setAttachmentUploading] = useState(new Set());

  useEffect(() => {
    if (!editingId) { setTaskAttachments([]); return; }
    api(`/api/tasks/${editingId}/attachments`)
      .then(r => r.json())
      .then(d => { if (Array.isArray(d.attachments)) setTaskAttachments(d.attachments); })
      .catch(() => {});
  }, [editingId]);

  const handleAttachFiles = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length || !editingId) return;
    e.target.value = '';
    for (const file of files) {
      setAttachmentUploading(prev => new Set([...prev, file.name]));
      try {
        const fd = new FormData();
        fd.append('file', file);
        const res = await api(`/api/tasks/${editingId}/attachments/upload`, { method: 'POST', body: fd });
        const data = await res.json();
        if (data.attachment) setTaskAttachments(prev => [data.attachment, ...prev]);
      } catch { /* ignore */ } finally {
        setAttachmentUploading(prev => { const s = new Set(prev); s.delete(file.name); return s; });
      }
    }
  };

  const handleDeleteAttachment = async (attachId) => {
    await api(`/api/tasks/${editingId}/attachments/${attachId}`, { method: 'DELETE' });
    setTaskAttachments(prev => prev.filter(a => a.id !== attachId));
  };

  // Private attachments (storageKey set) have no public URL — fetch a short-lived
  // pre-signed URL on demand and open it. Legacy/external rows keep their href.
  const openAttachment = async (a) => {
    try {
      const res = await api(`/api/tasks/${editingId}/attachments/${a.id}/url`);
      const data = await res.json();
      if (data.url) window.open(data.url, '_blank', 'noopener');
    } catch (err) {
      console.error('Failed to open attachment:', err);
    }
  };

  // ✅ Loading state for form submission
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Leave request submit handler
  const handleLeaveSubmit = async (e) => {
    e.preventDefault();
    if (isSubmitting) return;
    if (!startDate || !deadlineDate) {
      alert('Please select both a start date and end date for your leave request.');
      return;
    }
    if (workingDays <= 0) {
      alert('The selected date range contains no working days (Mon–Fri).');
      return;
    }
    if (leaveAllowance && taskType !== 'Sick' && workingDays > leaveAllowance.remaining) {
      // eslint-disable-next-line no-restricted-globals
      const ok = confirm(`You only have ${leaveAllowance.remaining} days remaining but are requesting ${workingDays}. Submit anyway?`);
      if (!ok) return;
    }

    setIsSubmitting(true);
    try {
      const res = await api('/api/leave/request', {
        method: 'POST',
        body: JSON.stringify({
          type: LEAVE_TYPE_MAP[taskType] || 'vacation',
          startDate: startDate.toISOString().split('T')[0],
          endDate: deadlineDate.toISOString().split('T')[0],
          daysRequested: workingDays,
          note: details || '',
        }),
      });
      const data = await res.json();
      if (!res.ok) {
        alert(data.error || 'Failed to submit leave request');
        return;
      }
      alert(`Your ${taskType.toLowerCase()} request has been submitted. Your manager will review it shortly.`);
      if (onCancel) onCancel();
    } catch {
      alert('Failed to submit leave request. Please check your connection and try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Enhanced form submit handler with validation and loading state
  const handleSubmit = async (e) => {
    e.preventDefault();

    // Prevent double submission
    if (isSubmitting) return;

    setIsSubmitting(true);

    try {
      // Validate location if meeting type is selected
      if (meetingType && location) {
        if (!validateLocation()) {
          if (meetingType === "Address") {
            // eslint-disable-next-line no-restricted-globals
            alert("Please enter a valid address for the physical location.");
            return;
          } else {
            // eslint-disable-next-line no-restricted-globals
            alert(`Please enter a valid URL for the ${meetingType} meeting link.`);
            return;
          }
        }
      }

      // If meeting type is selected but no location provided, show warning
      if (meetingType && !location) {
        // eslint-disable-next-line no-restricted-globals
        const shouldContinue = confirm(
          `You've selected "${meetingType}" as the meeting type but haven't provided a location. Continue without location?`
        );
        if (!shouldContinue) return;
      }

      // ✅ Process @mentions before submission
      const mentions = processMentionsOnSubmit();
      console.log('Task mentions processed:', mentions);

      // Pre-generate the task ID so pending attachments can be uploaded after creation
      const preId = (!editingId && (pendingFiles.length > 0 || pendingLinks.length > 0))
        ? Date.now()
        : null;
      if (preId) e._aideyTaskId = preId;

      // Call the original onSubmit handler
      await onSubmit(e);

      // Upload any pending attachments now that the task exists in the DB
      if (preId) {
        for (const file of pendingFiles) {
          try {
            const fd = new FormData();
            fd.append('file', file);
            await api(`/api/tasks/${preId}/attachments/upload`, { method: 'POST', body: fd });
          } catch { /* non-fatal */ }
        }
        for (const link of pendingLinks) {
          try {
            await api(`/api/tasks/${preId}/attachments`, {
              method: 'POST',
              body: JSON.stringify({ source: 'url', externalUrl: link }),
            });
          } catch { /* non-fatal */ }
        }
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      // eslint-disable-next-line no-restricted-globals
      alert('An error occurred while saving the task. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // Custom chip label for CreatableSelect — external emails get an envelope prefix
  const InviteeMultiValueLabel = ({ data, innerProps }) => (
    <div {...innerProps} style={{ padding: '0.1rem 0.35rem', fontSize: '0.8rem', color: '#0C2D48', fontWeight: 600, fontFamily: "'Raleway', sans-serif" }}>
      {data.user ? data.user.name : `✉️ ${data.label}`}
    </div>
  );

  // React-Select shared styles (used for both Invitees Select)
  const selectStyles = {
    control: (base, state) => ({
      ...base,
      border: state.isFocused ? '1.5px solid #0C2D48' : '1.5px solid #e0e3e8',
      borderRadius: '8px',
      minHeight: '40px',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(12,45,72,0.08)' : 'none',
      fontFamily: "'Raleway', sans-serif",
      fontSize: '0.88rem',
      '&:hover': { borderColor: '#0C2D48' },
    }),
    placeholder: (base) => ({ ...base, color: '#b0b8be', fontSize: '0.88rem' }),
    input: (base) => ({ ...base, color: '#0C2D48' }), // Block D3: ensure typed text is visible
    multiValue: (base) => ({ ...base, backgroundColor: '#e8edf5', borderRadius: '12px' }),
    multiValueLabel: (base) => ({ ...base, color: '#0C2D48', fontWeight: 600, fontSize: '0.8rem', padding: '0.15rem 0.45rem' }),
    multiValueRemove: (base) => ({ ...base, color: '#0C2D48', '&:hover': { background: '#0C2D48', color: '#fff' } }),
    menu: (base) => ({ ...base, borderRadius: '8px', border: '1px solid #e0e3e8', boxShadow: '0 6px 16px rgba(0,0,0,0.1)', zIndex: 9999 }),
    menuList: (base) => ({ ...base, padding: '0.4rem 0', maxHeight: '200px' }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#0C2D48' : state.isFocused ? '#f0f3f8' : '#fff',
      color: state.isSelected ? '#fff' : '#333',
      padding: '0.6rem 1rem',
      fontSize: '0.88rem',
    }),
  };

  // Build invitee options: team members + allow typing email addresses
  const inviteeOptions = availableUsers.map(u => ({
    value: u.id,
    label: u.name,
    user: u,
    type: 'member',
  }));
  // Block D3: the Select value must include external-email invitees too. They ARE
  // written into assignedTeam/selectedUsers on change, but they aren't in
  // teamMemberOptions, so selectedTeamMembers filtered them out and the typed address
  // silently vanished from the field. Re-add any selectedUsers value that isn't a
  // known member as an ✉️ chip so it stays visible (and removable).
  const memberIdSet = new Set(teamMemberOptions.map(o => o.value));
  const inviteeValue = [
    ...selectedTeamMembers,
    ...selectedUsers.filter(v => !memberIdSet.has(v)).map(email => ({ value: email, label: email })),
  ];

  const handleMeetingToggle = (e) => {
    if (e.target.checked) {
      if (defaultMeetingType && !["Custom Link", "Address"].includes(defaultMeetingType)) {
        setMeetingType(defaultMeetingType);
      } else {
        setMeetingType(googleMeetConnected ? "Google Meet" : zoomConnected ? "Zoom" : teamsConnected ? "Teams" : "Google Meet");
      }
    } else {
      setMeetingType(null);
    }
  };

  return (
    <div className="task-form-wrapper">
      <form className="task-form" onSubmit={isLeaveMode ? handleLeaveSubmit : handleSubmit}>

        {/* ══════════ GROUP 1: INFO ══════════ */}
        <div className="form-section">
          <div className="form-section-label">Info</div>

          {/* Task Title — full width */}
          <div className="form-group">
            <label htmlFor="task-title">Task Title</label>
            <input
              id="task-title"
              type="text"
              placeholder="Add title here"
              value={title}
              onChange={e => setTitle(e.target.value)}
              required
              autoFocus
            />
          </div>

          {/* Type | Priority — 50/50 */}
          <div className="form-row-2">
            <div className="form-group">
              <label htmlFor="task-type">Type</label>
              <select id="task-type" value={taskType} onChange={e => setTaskType(e.target.value)}>
                <option value="">Select type…</option>
                {taskTypes.map(t => (
                  <option key={t.value} value={t.value}>{t.label}</option>
                ))}
              </select>
            </div>
            <div className="form-group">
              <label htmlFor="priority">Priority</label>
              <select id="priority" value={priority} onChange={e => setPriority(e.target.value)}>
                <option value="Critical">🔴 Critical</option>
                <option value="High">🟠 High</option>
                <option value="Medium">🟡 Medium</option>
                <option value="Low">🟢 Low</option>
              </select>
            </div>
          </div>

          {/* Project — full width */}
          <div className="form-group">
            <label htmlFor="task-project">Project</label>
            <select
              id="task-project"
              value={project || ""}
              onChange={e => setProject && setProject(e.target.value || null)}
            >
              <option value="">No project</option>
              {projects.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </div>

          {/* Auto-privacy badge for personal task types */}
          {PERSONAL_TYPES.has(taskType) && (
            <div className="privacy-notice">
              🔒 Private task — only visible to you and managers
            </div>
          )}
        </div>

        {/* ══════════ GROUP 2: SCHEDULE ══════════ */}
        <div className="form-section">
          <div className="form-section-label">Schedule</div>

          {/* Start Date+Time | End Date+Time — 50/50 */}
          <div className="datetime-group">
            <div className="form-group">
              <label htmlFor="start-date">Start Date & Time</label>
              <div className="datetime-pair">
                <DatePicker
                  id="start-date"
                  selected={startDate || null}
                  onChange={handleStartDateChange}
                  placeholderText="Start date"
                  dateFormat="dd MMM yyyy"
                />
                <DatePicker
                  id="start-time"
                  selected={startTime || null}
                  onChange={handleStartTimeChange}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  placeholderText="Start time"
                />
              </div>
            </div>
            <div className="form-group">
              <label htmlFor="end-date">End Date & Time</label>
              <div className="datetime-pair">
                <DatePicker
                  id="end-date"
                  selected={deadlineDate || null}
                  onChange={handleDeadlineDateChange}
                  placeholderText="End date"
                  dateFormat="dd MMM yyyy"
                />
                <DatePicker
                  id="end-time"
                  selected={deadlineTime || null}
                  onChange={handleDeadlineTimeChange}
                  showTimeSelect
                  showTimeSelectOnly
                  timeIntervals={15}
                  timeCaption="Time"
                  dateFormat="h:mm aa"
                  placeholderText="End time"
                />
              </div>
            </div>
          </div>
          {dateRangeError && (
            <p style={{ color: '#dc2626', fontSize: '0.85rem', margin: '-0.25rem 0 0.75rem', fontFamily: "'Raleway', sans-serif", display: 'flex', alignItems: 'center', gap: '0.3rem' }}>
              <span aria-hidden="true">⚠</span> {dateRangeError}
            </p>
          )}

          {/* Leave request allowance indicator */}
          {isLeaveMode && (
            <div style={{
              padding: '0.7rem 0.9rem', marginBottom: '0.85rem',
              background: '#f0f7ff', border: '1.5px solid #c7deff',
              borderRadius: 9, fontFamily: "'Raleway', sans-serif", fontSize: '0.85rem',
            }}>
              {isLeaveLoadingAllowance ? (
                <span style={{ color: '#888' }}>Loading allowance…</span>
              ) : leaveAllowance ? (
                <div>
                  {taskType === 'Sick' ? (
                    <span style={{ color: '#555' }}>
                      Sick days are tracked separately and do not deduct from your holiday allowance.
                      {workingDays > 0 && <strong style={{ color: '#0C2D48', marginLeft: '0.4rem' }}>{workingDays} day{workingDays !== 1 ? 's' : ''} selected.</strong>}
                    </span>
                  ) : (
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.75rem', alignItems: 'center' }}>
                      <span>
                        <strong style={{ color: leaveAllowance.remaining > 5 ? '#1b5e20' : '#c0392b' }}>
                          {leaveAllowance.remaining} day{leaveAllowance.remaining !== 1 ? 's' : ''} remaining
                        </strong>
                        <span style={{ color: '#888' }}>
                          {' '}of {leaveAllowance.totalAllowance} total
                          {leaveAllowance.usedDays > 0 && ` · ${leaveAllowance.usedDays} used`}
                          {leaveAllowance.pendingDays > 0 && ` · ${leaveAllowance.pendingDays} pending`}
                        </span>
                      </span>
                      {workingDays > 0 && (
                        <span style={{ fontWeight: 700, color: leaveAllowance.remaining >= workingDays ? '#1b5e20' : '#c0392b' }}>
                          This request: {workingDays} working day{workingDays !== 1 ? 's' : ''}
                          {leaveAllowance.remaining < workingDays && ' ⚠ exceeds allowance'}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <span style={{ color: '#888' }}>Connect to your team to see your holiday allowance.</span>
              )}
            </div>
          )}

          {/* Reminder / Repeat / calendar block — hidden in leave request mode */}
          {/* Reminder | Repeat — 50/50 */}
          <div className="form-row-2" style={isLeaveMode ? { display: 'none' } : {}}>
            <div className="form-group">
              <label htmlFor="reminder">Reminder</label>
              <select id="reminder" value={reminderOffset} onChange={e => setReminderOffset(parseInt(e.target.value))}>
                <option value={0}>At time of event</option>
                <option value={5}>5 minutes before</option>
                <option value={10}>10 minutes before</option>
                <option value={15}>15 minutes before</option>
                <option value={30}>30 minutes before</option>
                <option value={60}>1 hour before</option>
                <option value={120}>2 hours before</option>
                <option value={1440}>1 day before</option>
                <option value={2880}>2 days before</option>
              </select>
            </div>
            <div className="form-group">
              <label>Repeat</label>
              <div
                className="repeat-toggle-row"
                onClick={() => setRecurEnabled(v => !v)}
              >
                <input
                  type="checkbox"
                  checked={recurEnabled}
                  onChange={e => setRecurEnabled(e.target.checked)}
                  onClick={e => e.stopPropagation()}
                />
                <span>Repeat this task</span>
              </div>
            </div>
          </div>

          {/* When repeat enabled: frequency select + detail controls (hidden in leave mode) */}
          {recurEnabled && !isLeaveMode && (
            <>
              <div className="form-group" style={{ marginTop: '0.65rem', marginBottom: '0.65rem' }}>
                <select
                  value={recurFrequency}
                  onChange={e => setRecurFrequency(e.target.value)}
                >
                  <option value="daily">Daily</option>
                  <option value="weekly">Weekly</option>
                  <option value="monthly">Monthly</option>
                  <option value="yearly">Yearly</option>
                  <option value="weekday">Every weekday (Mon–Fri)</option>
                  <option value="custom">Custom interval</option>
                </select>
              </div>

              <div className="recurrence-detail">
                {/* Custom: interval + unit */}
                {recurFrequency === 'custom' && (
                  <div className="recur-row">
                    <label>Every</label>
                    <input
                      type="number" min="1" max="99" value={recurInterval}
                      onChange={e => setRecurInterval(Math.max(1, parseInt(e.target.value) || 1))}
                    />
                    <select value={recurUnit} onChange={e => setRecurUnit(e.target.value)}>
                      <option value="days">days</option>
                      <option value="weeks">weeks</option>
                      <option value="months">months</option>
                    </select>
                  </div>
                )}
                {/* Weekly: day of week */}
                {recurFrequency === 'weekly' && (
                  <div className="recur-row">
                    <label>On</label>
                    <select value={recurDay} onChange={e => setRecurDay(e.target.value)}>
                      {['monday','tuesday','wednesday','thursday','friday','saturday','sunday'].map(d => (
                        <option key={d} value={d}>{d.charAt(0).toUpperCase() + d.slice(1)}</option>
                      ))}
                    </select>
                  </div>
                )}
                {/* Monthly: day of month */}
                {recurFrequency === 'monthly' && (
                  <div className="recur-row">
                    <label>On day</label>
                    <input
                      type="number" min="1" max="31" value={recurDate}
                      onChange={e => setRecurDate(Math.min(31, Math.max(1, parseInt(e.target.value) || 1)))}
                    />
                    <label>of the month</label>
                  </div>
                )}
                {/* End condition */}
                <div className="recur-row">
                  <label>Ends</label>
                  <select value={recurEndType} onChange={e => setRecurEndType(e.target.value)}>
                    <option value="never">Never</option>
                    <option value="count">After</option>
                    <option value="until">On date</option>
                  </select>
                  {recurEndType === 'count' && (
                    <>
                      <input
                        type="number" min="1" max="999" value={recurCount}
                        onChange={e => setRecurCount(Math.max(1, parseInt(e.target.value) || 1))}
                      />
                      <label>occurrences</label>
                    </>
                  )}
                  {recurEndType === 'until' && (
                    <DatePicker
                      selected={recurUntil}
                      onChange={setRecurUntil}
                      dateFormat="dd MMM yyyy"
                      placeholderText="End date"
                      minDate={new Date()}
                    />
                  )}
                </div>
              </div>
            </>
          )}

          {/* Edit scope — shown only when editing a recurring instance */}
          {editingId && recurrence && (
            <div className="form-group" style={{ marginTop: '0.65rem' }}>
              <label>Edit recurring event</label>
              <select value={recurEditScope || 'all'} onChange={e => setRecurEditScope && setRecurEditScope(e.target.value)}>
                <option value="this">This event only</option>
                <option value="following">This and following events</option>
                <option value="all">All events</option>
              </select>
            </div>
          )}

          {/* Block calendar — hidden in leave mode */}
          <div className="checkbox-row" style={{ marginTop: '0.65rem', ...(isLeaveMode ? { display: 'none' } : {}) }}>
            <input type="checkbox" id="busy" checked={busy} onChange={e => setBusy(e.target.checked)} />
            <label htmlFor="busy">Block this time on my calendar</label>
          </div>
        </div>

        {/* ══════════ GROUP 3: LOCATION — hidden in leave mode ══════════ */}
        <div className="form-section" style={isLeaveMode ? { display: 'none' } : {}}>
          <div className="form-section-label">Location</div>

          <div className="form-group">
            <label htmlFor="location-input">Address or Meeting Link</label>
            <LocationInput
              id="location-input"
              value={location || ''}
              onChange={val => setLocation(val)}
              placeholder="Enter a physical address or paste a link"
            />

            {/* Travel time toggle */}
            <div className="meeting-toggle-row" style={{ marginTop: '0.5rem' }}>
              <input
                type="checkbox"
                id="travelTime"
                checked={showTravelTime}
                onChange={e => setShowTravelTime(e.target.checked)}
              />
              <label htmlFor="travelTime">Add travel time</label>
            </div>

            {showTravelTime && (
              <div style={{ marginTop: '0.5rem' }}>
                <label style={{ display: 'block', fontFamily: "'Raleway', sans-serif", fontSize: '0.8rem', fontWeight: 700, color: '#444', marginBottom: '0.3rem' }}>
                  Travelling from
                </label>
                <select
                  value={travelFrom}
                  onChange={e => setTravelFrom(e.target.value)}
                  style={{ width: '100%', height: '40px', padding: '0 0.85rem', border: '1.5px solid #e0e3e8', borderRadius: '8px', fontFamily: "'Raleway', sans-serif", fontSize: '0.88rem', background: '#fff', color: '#242629', boxSizing: 'border-box' }}
                >
                  <option value="">Select starting location…</option>
                  <option value="previous">Previous meeting location</option>
                  <option value="home">Home</option>
                  <option value="office">Office</option>
                  <option value="custom">Custom address</option>
                </select>
                {travelFrom === 'custom' && (
                  <input
                    type="text"
                    placeholder="Enter starting address"
                    value={customTravelAddress}
                    onChange={e => setCustomTravelAddress(e.target.value)}
                    style={{ width: '100%', height: '40px', padding: '0 0.85rem', border: '1.5px solid #e0e3e8', borderRadius: '8px', marginTop: '0.5rem', fontFamily: "'Raleway', sans-serif", fontSize: '0.88rem', boxSizing: 'border-box', background: '#fff', color: '#242629' }}
                  />
                )}

                {/* Travel time result */}
                {travelInfoLoading && (
                  <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.5rem' }}>
                    Calculating travel time…
                  </div>
                )}
                {!travelInfoLoading && travelInfo && (() => {
                  const tp = currentUser?.preferences?.travel || {};
                  const originAddr = travelFrom === 'home' ? tp.homeAddress : travelFrom === 'office' ? tp.workplaceAddress : customTravelAddress;
                  const mapsUrl = originAddr && location
                    ? `https://www.google.com/maps/dir/${encodeURIComponent(originAddr)}/${encodeURIComponent(location)}`
                    : null;
                  return (
                    <div style={{ marginTop: '0.5rem', padding: '0.5rem 0.75rem', background: '#f0f4f8', borderRadius: 6, fontSize: '0.8rem', color: '#333', lineHeight: 1.6 }}>
                      <span style={{ fontWeight: 700 }}>{MODE_EMOJI[travelInfo.recommendedMode] || '🚗'}</span>
                      {' '}~{travelInfo.travelMins} min travel · {travelInfo.bufferMins} min with buffer
                      {travelInfo.distanceText && <span style={{ color: '#888' }}> · {travelInfo.distanceText}</span>}
                      {mapsUrl && (
                        <span>
                          {' · '}
                          <a href={mapsUrl} target="_blank" rel="noreferrer" style={{ color: '#0C2D48', fontWeight: 700 }}>
                            Open in Maps
                          </a>
                        </span>
                      )}
                      {travelInfo.needsHotel && (
                        <div style={{ color: '#E96A6A', marginTop: '0.25rem', fontSize: '0.75rem' }}>
                          ⚠️ Over 150 km — consider overnight accommodation
                        </div>
                      )}
                    </div>
                  );
                })()}
                {!travelInfoLoading && !travelInfo && travelFrom && travelFrom !== 'custom' && location && (() => {
                  const tp = currentUser?.preferences?.travel || {};
                  const addr = travelFrom === 'home' ? tp.homeAddress : tp.workplaceAddress;
                  if (!addr) return (
                    <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.5rem' }}>
                      Save your {travelFrom} address in Travel Preferences to auto-calculate.
                    </div>
                  );
                  const buffer = tp.travelBuffer || 60;
                  return (
                    <div style={{ fontSize: '0.78rem', color: '#888', marginTop: '0.5rem' }}>
                      ~{buffer} min travel buffer (from your Travel Preferences)
                    </div>
                  );
                })()}
              </div>
            )}

            {/* Create meeting link toggle */}
            <div className="meeting-toggle-row" style={{ marginTop: '0.5rem' }}>
              <input
                type="checkbox"
                id="onlineMeeting"
                checked={!!meetingType}
                onChange={handleMeetingToggle}
              />
              <label htmlFor="onlineMeeting">
                Create online meeting link
                {meetingType && <span style={{ color: '#888', fontWeight: 400 }}>
                  {meetingType === "Google Meet" ? " 🎥" : meetingType === "Zoom" ? " 📹" : meetingType === "Teams" ? " 💼" : ""}
                </span>}
              </label>
            </div>

            {/* Meeting service panel */}
            {meetingType && (
              <div className="meeting-service-panel">
                {(!defaultMeetingType || ["Custom Link", "Address"].includes(defaultMeetingType)) && (
                  <div className="service-btns">
                    {["Google Meet", "Zoom", "Teams"].map(svc => (
                      <button
                        key={svc}
                        type="button"
                        className={`service-btn${meetingType === svc ? " active" : ""}`}
                        onClick={() => setMeetingType(svc)}
                      >
                        {svc === "Google Meet" ? "🎥" : svc === "Zoom" ? "📹" : "💼"} {svc}
                      </button>
                    ))}
                  </div>
                )}
                {!["Custom Link", "Address"].includes(meetingType) && (
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <button
                      type="button"
                      onClick={() => {
                        if (meetingType === "Zoom") handleGenerateZoomLink();
                        else if (meetingType === "Google Meet") handleGenerateGoogleMeetLink();
                        else if (meetingType === "Teams") handleGenerateTeamsLink();
                      }}
                      disabled={
                        (meetingType === "Zoom" && (isZoomGenerating || !zoomConnected)) ||
                        (meetingType === "Google Meet" && (isGoogleMeetGenerating || !googleMeetConnected)) ||
                        (meetingType === "Teams" && (isTeamsGenerating || !teamsConnected))
                      }
                      style={{
                        padding: '0.45rem 1rem', borderRadius: 6, border: 'none',
                        background: '#1976d2', color: '#fff',
                        fontSize: '0.83rem', fontWeight: 600, cursor: 'pointer',
                        opacity: (isZoomGenerating || isGoogleMeetGenerating || isTeamsGenerating) ? 0.6 : 1,
                      }}
                    >
                      {isZoomGenerating || isGoogleMeetGenerating || isTeamsGenerating ? "Creating…" : `Generate ${meetingType} Link`}
                    </button>
                    <small style={{ color: '#888', fontSize: '0.78rem' }}>
                      {meetingType === "Zoom" && (zoomConnected ? "✅ Zoom connected" : "⚠️ Connect Zoom in Settings → Video meetings")}
                      {meetingType === "Google Meet" && (googleMeetConnected ? "✅ Google Meet connected" : "⚠️ Connect Google Meet in Settings → Video meetings")}
                      {meetingType === "Teams" && (teamsConnected ? "✅ Teams connected" : "⚠️ Connect Teams in Settings → Video meetings")}
                    </small>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>

        {/* ══════════ GROUP 4: DETAILS ══════════ */}
        <div className="form-section">
          <div className="form-section-label">Details</div>

          {/* Notes & Description (also used as leave request note) */}
          <div className="form-group mention-input-container" style={{ position: 'relative' }}>
            <label htmlFor="task-details">{isLeaveMode ? 'Note (optional)' : 'Notes & Description'}</label>
            <textarea
              id="task-details"
              placeholder={isLeaveMode ? 'Add a note for your manager…' : 'Add notes or details… Use @name to mention team members'}
              value={detailsWithMentions}
              onChange={handleDescriptionChange}
              onKeyDown={handleMentionKeyDown}
              style={{ minHeight: '90px', resize: 'vertical' }}
            />
            {/* @Mention dropdown */}
            {showMentionDropdown && mentionSuggestions.length > 0 && (
              <div style={{
                position: 'absolute', top: '100%', left: 0, right: 0,
                background: '#fff', border: '1px solid #e0e3e8',
                borderRadius: 8, boxShadow: '0 6px 16px rgba(0,0,0,0.1)',
                zIndex: 1000, maxHeight: 200, overflowY: 'auto',
              }}>
                {mentionSuggestions.map((user, i) => (
                  <div
                    key={user.id}
                    onClick={() => handleMentionSelect(user)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 10,
                      padding: '10px 14px', cursor: 'pointer',
                      borderBottom: i < mentionSuggestions.length - 1 ? '1px solid #f0f0f0' : 'none',
                    }}
                    onMouseEnter={e => e.currentTarget.style.background = '#f8f9fa'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
                  >
                    <div style={{
                      width: 30, height: 30, borderRadius: '50%',
                      background: user.color || '#4F46E5',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: 13, fontWeight: 700, flexShrink: 0,
                    }}>
                      {user.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: '#222' }}>{user.name}</div>
                      <div style={{ fontSize: '0.76rem', color: '#888' }}>{user.email}</div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Invitees (Business/Team) or household member (Personal/Family) — hidden in leave request mode */}
          {isPersonalWorkspace ? (
            <div className="form-group" style={isLeaveMode ? { display: 'none' } : {}}>
              <label htmlFor="household-member">For (optional)</label>
              {householdMembers.length > 0 ? (
                <select
                  id="household-member"
                  className="form-control"
                  value={householdMemberId || ''}
                  onChange={e => setHouseholdMemberId(e.target.value || null)}
                >
                  <option value="">Whole household</option>
                  {householdMembers.map(m => <option key={m.id} value={m.id}>{m.name}</option>)}
                </select>
              ) : (
                <>
                  <select id="household-member" className="form-control" value="" disabled style={{ color: '#9ca3af', cursor: 'not-allowed' }}>
                    <option value="">Whole household</option>
                  </select>
                  <div style={{ fontSize: 12, color: '#9ca3af', marginTop: 4 }}>No household members yet — add them in Family to tag tasks.</div>
                </>
              )}
            </div>
          ) : (
            <div className="form-group team-assignment" style={isLeaveMode ? { display: 'none' } : {}}>
              <label htmlFor="invitees">Invitees</label>
              <CreatableSelect
                inputId="invitees"
                isMulti
                value={inviteeValue}
                onChange={handleTeamAssignmentChange}
                options={inviteeOptions}
                placeholder="@mention team members or enter an email…"
                closeMenuOnSelect={false}
                isSearchable
                isClearable
                formatCreateLabel={input => `Add "${input}" as external invitee`}
                isValidNewOption={input => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(input)}
                components={{ MultiValueLabel: InviteeMultiValueLabel }}
                styles={selectStyles}
                formatOptionLabel={opt => opt.user ? (
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                    <div style={{
                      width: 28, height: 28, borderRadius: '50%',
                      background: opt.user.color || '#0C2D48',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: '#fff', fontSize: '0.78rem', fontWeight: 700, flexShrink: 0,
                    }}>
                      {opt.user.name.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem' }}>
                        {opt.user.name}
                        {opt.user.id === currentUser?.id && (
                          <span style={{ marginLeft: 6, fontSize: '0.68rem', padding: '0.15rem 0.35rem', background: '#e8f5e8', color: '#2e7d32', borderRadius: 4, fontWeight: 700, textTransform: 'uppercase' }}>
                            You
                          </span>
                        )}
                      </div>
                      <div style={{ fontSize: '0.76rem', color: '#888' }}>{opt.user.email}</div>
                    </div>
                  </div>
                ) : (
                  <span style={{ fontSize: '0.88rem', color: '#555' }}>✉️ {opt.label}</span>
                )}
              />
            </div>
          )}
        </div>

        {/* ══════════ GROUP 5: ATTACHMENTS ══════════ */}
        <div className="form-section">
          <div className="form-section-label">Attachments</div>
          {!editingId ? (
            <>
              {/* Pending files list */}
              {pendingFiles.map((f, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 7, border: '1px solid #e0e3e8', background: '#f7f9fc', marginBottom: 6 }}>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>📎 {f.name}</span>
                  <button type="button" onClick={() => setPendingFiles(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: 18, lineHeight: 1, padding: 2 }} title="Remove">×</button>
                </div>
              ))}
              {/* Pending links list */}
              {pendingLinks.map((link, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 7, border: '1px solid #e0e3e8', background: '#f7f9fc', marginBottom: 6 }}>
                  <a href={link} target="_blank" rel="noopener noreferrer" style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#0C2D48', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'none' }}>🔗 {link}</a>
                  <button type="button" onClick={() => setPendingLinks(prev => prev.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: 18, lineHeight: 1, padding: 2 }} title="Remove">×</button>
                </div>
              ))}
              {/* File picker */}
              <label style={{ display: 'inline-block', background: '#f7f9fc', border: '1px solid #e0e3e8', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#0C2D48', marginBottom: 8 }}>
                + Attach files
                <input ref={pendingFileInputRef} type="file" multiple accept="*/*" onChange={e => { const files = Array.from(e.target.files || []); if (files.length) setPendingFiles(prev => [...prev, ...files]); e.target.value = ''; }} style={{ display: 'none' }} />
              </label>
              {/* Link input */}
              <div style={{ display: 'flex', gap: 8, marginTop: 4 }}>
                <input
                  type="url"
                  placeholder="Paste a link…"
                  value={linkInput}
                  onChange={e => setLinkInput(e.target.value)}
                  onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddPendingLink(); } }}
                  style={{ flex: 1, height: 36, padding: '0 0.75rem', border: '1.5px solid #e0e3e8', borderRadius: 7, fontSize: 13, fontFamily: 'inherit', boxSizing: 'border-box', minWidth: 0, fontSize: '16px' }}
                />
                <button
                  type="button"
                  onClick={handleAddPendingLink}
                  style={{ padding: '0 14px', height: 36, background: '#f7f9fc', border: '1px solid #e0e3e8', borderRadius: 7, fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#0C2D48', whiteSpace: 'nowrap', minWidth: 44 }}
                >
                  Add link
                </button>
              </div>
            </>
          ) : (
            <>
              {taskAttachments.map(a => (
                <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 7, border: '1px solid #e0e3e8', background: '#fff', marginBottom: 6 }}>
                  <span style={{ flex: 1, fontSize: 13, fontWeight: 600, color: '#222', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.fileName}</span>
                  {a.storageKey ? (
                    <button type="button" onClick={() => openAttachment(a)} style={{ fontSize: 12, color: '#0C2D48', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}>Download</button>
                  ) : (
                    <a href={a.fileUrl || a.externalUrl} target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#0C2D48', fontWeight: 700, textDecoration: 'none', whiteSpace: 'nowrap' }}>Download</a>
                  )}
                  <button type="button" onClick={() => handleDeleteAttachment(a.id)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#bbb', fontSize: 18, lineHeight: 1, padding: 2 }} title="Remove">×</button>
                </div>
              ))}
              {[...attachmentUploading].map(name => (
                <div key={name} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px', borderRadius: 7, border: '1px dashed #e0e3e8', background: '#f7f9fc', marginBottom: 6 }}>
                  <span style={{ flex: 1, fontSize: 13, color: '#888', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{name}</span>
                  <span style={{ fontSize: 11, color: '#aaa' }}>Uploading…</span>
                </div>
              ))}
              {taskAttachments.length === 0 && attachmentUploading.size === 0 && (
                <p style={{ color: '#aaa', fontSize: 13, margin: '0 0 8px' }}>No attachments yet.</p>
              )}
              <label style={{ display: 'inline-block', background: '#f7f9fc', border: '1px solid #e0e3e8', borderRadius: 7, padding: '7px 14px', fontSize: 12, fontWeight: 700, cursor: 'pointer', color: '#0C2D48' }}>
                + Attach files
                <input type="file" multiple accept="*/*" onChange={handleAttachFiles} style={{ display: 'none' }} />
              </label>
            </>
          )}
        </div>

        {/* ══════════ CLASH WARNING ══════════ */}
        {clashWarning && (
          <div style={{
            background: 'var(--clash-bg, #fffbeb)',
            border: '1px solid var(--clash-border, #f59e0b)',
            borderRadius: 8,
            padding: '10px 14px',
            marginBottom: 12,
            fontSize: 14,
            color: 'var(--clash-text, #92400e)',
          }}>
            This overlaps with "{clashWarning.conflictTitle}"{clashWarning.conflictTime ? ` at ${clashWarning.conflictTime}` : ''}.
            <div style={{ marginTop: 8, display: 'flex', gap: 8 }}>
              <button
                type="button"
                onClick={onSaveAnyway}
                style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #f59e0b', background: '#f59e0b', color: '#fff', cursor: 'pointer', fontSize: 13 }}
              >
                Save anyway
              </button>
              <button
                type="button"
                onClick={onChangeTime}
                style={{ padding: '4px 12px', borderRadius: 6, border: '1px solid #d1d5db', background: 'transparent', cursor: 'pointer', fontSize: 13 }}
              >
                Change time
              </button>
            </div>
          </div>
        )}

        {/* ══════════ FOOTER ══════════ */}
        <div className="modal-footer">
          {onCancel && (
            <button type="button" onClick={onCancel} disabled={isSubmitting} className="btn-cancel">
              Cancel
            </button>
          )}
          {isPro && editingId && !isLeaveMode && (
            <button
              type="button"
              onClick={() => setShowLogTime(true)}
              style={{ padding: '0.5rem 0.9rem', background: '#f0f4f8', border: '1px solid #dde6ef', borderRadius: 8, fontWeight: 600, fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit', color: '#444', display: 'flex', alignItems: 'center', gap: '0.35rem' }}
            >
              ⏱ Log Time
            </button>
          )}
          <button type="submit" disabled={isSubmitting || !!dateRangeError} className="btn-primary">
            {isSubmitting && (
              <div style={{
                width: 14, height: 14,
                border: '2px solid rgba(12,45,72,0.2)',
                borderTopColor: '#0C2D48',
                borderRadius: '50%',
                animation: 'spin 0.8s linear infinite',
              }} />
            )}
            {isSubmitting
              ? (isLeaveMode ? "Submitting…" : editingId ? "Updating…" : "Adding…")
              : (isLeaveMode ? "Submit Request" : editingId ? "Update Task" : "Add Task")
            }
          </button>
        </div>

      </form>

      {showLogTime && (
        <Suspense fallback={null}>
          <LogTimeModal
            task={{ id: editingId, title, projectId: project?.value ?? null }}
            onClose={() => setShowLogTime(false)}
            onSaved={() => setShowLogTime(false)}
          />
        </Suspense>
      )}
    </div>
  );
}

export default TaskForm;
